import { getDoc2BrandMap, getIgnoredDocuments } from 'utils/documentMaps';
import store from '@/store';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { decodePassengers } from 'utils/Reserbus';
import { times } from 'lodash';
import { getCountryCallingCode } from 'react-phone-number-input';
import { getVivaPnrFromPurchase } from 'selectors/viajeAhorrandoSelector';
import { getStoredPassengers } from './storedPassengers';
import { getUserByWalletType } from './loyalty';
import { isHybridTrip } from './wayIsOpenTicket';
import normalizePhone from './normalizePhone';
import { isDotersFlexibleRedeemEnabled, isAccountHolder } from './dotersFlexibleEditing';

export const DEFAULT_CATEGORY = 'general';
export const OVERBOOKED_CATEGORY = 'oversold';

// Order used when expanding passengerSelection into one slot per traveler (see getCountableCategories).
// Includes RJ/Unida gratuity keys so slots match search (e.g. idoso). Unknown keys are appended afterward.
const orderedPassengerCategories = [
  'general',
  'adult',
  'older',
  'older_half',
  'disability',
  'free_pass',
  'special',
  'disadvantaged_youth',
  'disadvantaged_youth_half',
  'teacher',
  'student',
  'wheelchair_handicap',
  'minor',
  'child_in_arms',
  'pet_friendly',
  'intermunicipal_free_pass',
];

/**
 * Removes adjacent seats from the provided array of seats.
 * @param {Array} seats - The array of seats.
 * @returns {Array} filteredSeats - The filtered array of seats without adjacent seats.
 */
const removeAdjacentSeats = (seats) => {
  return seats.filter((seat) => !seat.isPickedAsAdjacent);
};

/**
 * Returns the countable categories based on the provided categories.
 * @param {Object} params - The parameters object.
 * @param {Object|String} params.categories - The categories object or string to be decode.
 * @returns {Array} countableCategories - The countable categories.
 */
export const getCountableCategories = ({ categories }) => {
  let decodedPassengers;
  if (typeof categories === 'string') {
    decodedPassengers = decodePassengers(categories);
  } else if (categories && typeof categories === 'object') {
    decodedPassengers = categories;
  } else {
    decodedPassengers = {};
  }
  const countableCategories = [];
  orderedPassengerCategories.forEach((category) => {
    const passengerCategory = decodedPassengers[category];
    if (passengerCategory)
      countableCategories.push(...times(decodedPassengers[category], () => category));
  });
  const handled = new Set(orderedPassengerCategories);
  Object.keys(decodedPassengers).forEach((key) => {
    if (handled.has(key)) return;
    const count = decodedPassengers[key];
    if (!count) return;
    countableCategories.push(...times(count, () => key));
  });
  return countableCategories;
};

/**
 * Ordering rank for one expanded slot (lower runs first in passenger list).
 * @param {string} slot - Category key from {@link getCountableCategories}.
 * @returns {number}
 */
const fareSlotSortRank = (slot) => {
  const tier = {
    free_pass: 10,
    intermunicipal_free_pass: 11,
    // PCD search slots map to passe livre (`free_pass`) on many Unida lines — before idoso in product ladder
    disability: 18,
    special: 18,
    wheelchair_handicap: 18,
    older: 20,
    older_half: 21,
    disadvantaged_youth: 30,
    disadvantaged_youth_half: 31,
  };
  if (tier[slot] !== undefined) return tier[slot];
  if (slot === DEFAULT_CATEGORY) return 10000;
  if (slot === 'adult') return 10001;
  const idx = orderedPassengerCategories.indexOf(slot);
  if (idx !== -1) return 40 + idx;
  return 5000;
};

/**
 * Sort expanded search slots so higher-priority fare types map to earlier passengers (Unida-style).
 * Priority: Passe livre (`free_pass` / `intermunicipal_free_pass`) > PCD / accessibility (`disability`,
 * `special`, `wheelchair_handicap`; often shown as passe livre on the line) > Idoso (`older` / `older_half`) >
 * Jovem baixa renda (`disadvantaged_youth` / half) > Adulto (general / adult). Other known keys fall between jovem and adult.
 *
 * Idoso 100% vs 50% per traveler is still resolved later by {@link getPassengerCategory} + `busCategories`.
 *
 * @param {string[]|undefined|null} slots - From {@link getCountableCategories}
 * @returns {string[]|undefined|null}
 */
export const sortPassengerSlotsByFarePriority = (slots) => {
  if (!slots?.length) return slots;
  return [...slots].sort((a, b) => fareSlotSortRank(a) - fareSlotSortRank(b));
};

/** Slots that map to “full fare” / adult pricing on most lines (`adult` often collapses to `general`). */
const GENERIC_PASSENGER_SLOT_TYPES = new Set([DEFAULT_CATEGORY, 'adult']);

/**
 * Returns true when every expanded passenger slot is general or adult.
 * @param {object|undefined|null} selection - Passenger counts keyed by category.
 * @returns {boolean}
 */
const passengerSelectionIsGenericSlotsOnly = (selection) => {
  const slots = getCountableCategories({ categories: decamelizeKeys(selection || {}) });
  return slots.length > 0 && slots.every((slot) => GENERIC_PASSENGER_SLOT_TYPES.has(slot));
};

/**
 * When the purchase payload only records generic passenger counts but Redux search still has
 * gratuity breakdown (same traveler count), use search for per-slot categories.
 */
const shouldUseSearchPassengersForCategorySlots = (purchaseSelection, searchSelection) => {
  const purchaseSlots = getCountableCategories({
    categories: decamelizeKeys(purchaseSelection || {}),
  });
  const searchSlots = getCountableCategories({
    categories: decamelizeKeys(searchSelection || {}),
  });
  if (!searchSlots.length) return false;
  if (!purchaseSlots.length) return true;
  if (purchaseSlots.length !== searchSlots.length) return false;
  if (!passengerSelectionIsGenericSlotsOnly(purchaseSelection)) return false;
  return searchSlots.some((slot) => !GENERIC_PASSENGER_SLOT_TYPES.has(slot));
};

/**
 * Reorders passengers by moving minor to the end of the list.
 * @param {Array} passengers - List of passengers to reorder.
 * @returns {Array} Reordered list of passengers.
 */
export const reorderPassengers = (passengers) => {
  if (passengers.length <= 1) {
    return passengers;
  }

  /**
   * Reorder passengers on the array based on orderedPassengerCategories
   */
  return passengers.sort((a, b) => {
    const aIndex = orderedPassengerCategories.indexOf(a.busCategory || a.bus_category);
    const bIndex = orderedPassengerCategories.indexOf(b.busCategory || b.bus_category);

    if (aIndex === -1 || bIndex === -1) {
      return 0;
    }

    return aIndex - bIndex;
  });
};

/**
 * Returns the Document options for the passengers in the current brand
 * @returns {Object} passengersDocumentsOptions - The document options for the passengers
 */
const getDefaultPassengersDocuments = () => {
  const doc2BrandMapPassengers = getDoc2BrandMap('passengers');
  const doc2BrandMapPassengersSecond = getDoc2BrandMap('passengersSecond');
  const passengersDocumentsOptions = Object.values(doc2BrandMapPassengers);
  const passengersDocumentsSecondOptions = Object.values(doc2BrandMapPassengersSecond);
  const passengersDocumentsOptionsKeys = Object.keys(doc2BrandMapPassengers);

  const passengersDocumentsToIgnore = getIgnoredDocuments();
  const availableDocumentsForFirstPassengerKeys = passengersDocumentsOptionsKeys.filter(
    (option) => !passengersDocumentsToIgnore.includes(option),
  );
  const availableDocumentsForFirstPassenger = Object.entries(doc2BrandMapPassengers)
    .filter(([key]) => availableDocumentsForFirstPassengerKeys.includes(key))
    // eslint-disable-next-line no-unused-vars
    .map(([_, value]) => value);

  return {
    passengersDocumentsOptions,
    passengersDocumentsSecondOptions,
    availableDocumentsForFirstPassenger,
  };
};

/**
 * Pick full gratuity (100%) over half (50%) when both exist on the line config.
 * @param {Set<string>} availableTypes - `busCategories[].type`
 * @param {string} fullKey - e.g. `older`
 * @param {string} halfKey - e.g. `older_half`
 * @returns {string|null} Resolved type or null if neither exists
 */
const pickFullGratuityOverHalf = (availableTypes, fullKey, halfKey) => {
  const hasFull = availableTypes.has(fullKey);
  const hasHalf = availableTypes.has(halfKey);
  if (hasFull && hasHalf) return fullKey;
  if (hasFull) return fullKey;
  if (hasHalf) return halfKey;
  return null;
};

/** Search URL keys that represent PCD / accessibility slots (see fare slot ordering). */
const PCD_SLOT_CATEGORY_KEYS = new Set(['disability', 'special', 'wheelchair_handicap']);

/**
 * Bus `category_type` values that can satisfy a PCD search slot. Unida lines often expose PCD as `free_pass`
 * (Passe livre) while search still uses `disability`.
 */
const PCD_BUS_CATEGORY_FALLBACK_ORDER = [
  'disability',
  'free_pass',
  'special',
  'wheelchair_handicap',
];

/**
 * First matching type present on the line.
 * @param {Set<string>} availableTypes - `busCategories[].type`
 * @param {string[]} orderedCandidates - Preference order
 * @returns {string|null}
 */
const pickFirstAvailableBusCategoryType = (availableTypes, orderedCandidates) =>
  orderedCandidates.find((type) => availableTypes.has(type)) || null;

/**
 * Returns the passenger category available
 * @param {Object} params - Method params
 * @param {String} params.passengerBusCategory - The passenger category
 * @param {Array} params.busCategories - The bus categories available
 * @param {Number} params.index - The index of the passenger
 * @returns {String} category - The default passenger category
 */
export const getPassengerCategory = ({ passengerBusCategory, busCategories, index }) => {
  // The first passenger can't be a minor because they cannot travel alone
  const isMinorAlone = passengerBusCategory === 'minor' && index === 0;
  if (!busCategories || isMinorAlone) return DEFAULT_CATEGORY;

  const availableTypes = new Set(busCategories.map((item) => item.type));

  let categoryKey = passengerBusCategory;
  if (categoryKey === 'adult' && !availableTypes.has('adult')) {
    categoryKey = DEFAULT_CATEGORY;
  }

  if (categoryKey === 'older' || categoryKey === 'older_half') {
    const resolved = pickFullGratuityOverHalf(availableTypes, 'older', 'older_half');
    if (resolved) return resolved;
    return DEFAULT_CATEGORY;
  }

  if (categoryKey === 'disadvantaged_youth' || categoryKey === 'disadvantaged_youth_half') {
    const resolved = pickFullGratuityOverHalf(
      availableTypes,
      'disadvantaged_youth',
      'disadvantaged_youth_half',
    );
    if (resolved) return resolved;
    return DEFAULT_CATEGORY;
  }

  if (PCD_SLOT_CATEGORY_KEYS.has(categoryKey)) {
    const ordered = [
      categoryKey,
      ...PCD_BUS_CATEGORY_FALLBACK_ORDER.filter((k) => k !== categoryKey),
    ];
    const resolved = pickFirstAvailableBusCategoryType(availableTypes, ordered);
    if (resolved) return resolved;
    return DEFAULT_CATEGORY;
  }

  const category = busCategories.find((item) => item.type === categoryKey);
  return category?.type || DEFAULT_CATEGORY;
};

/**
 * Returns the default passenger data
 * @param {Object} params - The params object
 * @param {Boolean} params.isFirstPassenger - The flag to check if the passenger is the first one
 * @param {Object} params.passenger - The passenger data available
 * @param {String} params.seat - The seat of the passenger
 * @returns {Object} passenger - The default passenger data
 */
const createPassenger = ({ isFirstPassenger, passenger, seat }) => {
  const {
    whitelabelConfig: { env },
  } = store.getState();
  const {
    availableDocumentsForFirstPassenger,
    passengersDocumentsOptions,
    passengersDocumentsSecondOptions,
  } = getDefaultPassengersDocuments();

  const passengerCamelized = passenger && camelizeKeys(passenger);

  // Resolve firstName with fallback to name (for APIs that send name instead of first_name)
  const resolvedFirstName = passengerCamelized?.firstName ?? passengerCamelized?.name ?? '';

  const phoneCountry = passengerCamelized?.phoneCountry || env.lang?.default?.substring(3) || '';
  const phoneCode = getCountryCallingCode(phoneCountry) || '';
  let phone = passengerCamelized?.phone || '';
  if (passengerCamelized?.phoneCountry) {
    phone = normalizePhone(phone, phoneCountry);
  }

  let defaultDocumentType;
  if (env.brand === 'cds') {
    defaultDocumentType = 'DNI';
  } else if (isFirstPassenger) {
    defaultDocumentType = availableDocumentsForFirstPassenger[0] || 'CC';
  } else {
    defaultDocumentType = passengersDocumentsOptions[0] || 'CC';
  }

  return {
    id: passengerCamelized?.id || '',
    firstName: resolvedFirstName,
    secondFirstName: passengerCamelized?.secondFirstName ?? '',
    lastName: passengerCamelized?.lastName ?? '',
    secondLastName: passengerCamelized?.secondLastName ?? '',
    email: passengerCamelized?.email || '',
    phone,
    phoneCountry,
    phoneCode,
    name:
      (resolvedFirstName && `${resolvedFirstName} ${passengerCamelized?.lastName ?? ''}`.trim()) ||
      passengerCamelized?.name ||
      '',
    category: passengerCamelized?.category || 'adult',
    busCategory: passengerCamelized?.busCategory || DEFAULT_CATEGORY,
    documentType: passengerCamelized?.documentType || defaultDocumentType,
    documentTypeSecond:
      passengerCamelized?.documentTypeSecond || passengersDocumentsSecondOptions[0],
    wantsOutgoingInsurance: passengerCamelized?.wantsOutgoingInsurance ?? false,
    wantsIncomingInsurance: passengerCamelized?.wantsIncomingInsurance ?? false,
    nationality: passengerCamelized?.nationality || passengerCamelized?.isoCountryCode,
    dateOfBirth: passengerCamelized?.dateOfBirth ?? '',
    document: passengerCamelized?.document ?? '',
    documentId: passengerCamelized?.documentId ?? '',
    documentIdSecond: passengerCamelized?.documentIdSecond ?? '',
    gender: passengerCamelized?.gender ?? '',
    minorTravelMode: passengerCamelized?.minorTravelMode ?? '',
    isFrequentPassenger: passengerCamelized?.isFrequentPassenger || false,
    preferredPassengerOrder:
      passengerCamelized?.preferredPassengerOrder !== undefined
        ? passengerCamelized.preferredPassengerOrder
        : false,
    source: passengerCamelized?.source,
    meta: {
      ...passengerCamelized?.meta,
      lapChild: {
        documentType: 'CPF',
        documentId: '',
        firstName: '',
        lastName: '',
        birthDate: '',
        ...passengerCamelized?.meta?.lapChild,
      },
    },
    ...(seat && seat),
  };
};

/**
 * Returns the selected seat data by passenger usable for the UI
 * @param {Object} params - The params object
 * @param {Object} params.passengers - The passengers
 * @param {Array} params.departSeats - The departure seats
 * @param {Array} params.returnSeats - The return seats
 * @returns
 */
export const getSelectedSeatForUI = ({ passengers, departSeats, returnSeats }) => {
  return passengers.map((passenger, index) => {
    const departAssignedSeat = departSeats && departSeats[index];
    const returnAssignedSeat = returnSeats && returnSeats[index];

    return {
      ...passenger,
      seats: {
        ...(departAssignedSeat && { departure: departAssignedSeat }),
        ...(returnAssignedSeat && { return: returnAssignedSeat }),
      },
    };
  });
};

/**
 * Returns the priority passenger data
 * @param {Object} params - The params object
 * @param {Object} params.hasSeats - The way has seats selected
 * @param {Number} params.seatsNeeded - The seats needed
 * @param {Number} params.searchedPassengers - Passengers categories used for the search
 * @param {Boolean} params.allowsSeatSelection - The way allows seat selection
 * @param {Boolean} params.overbooking - Whether overbooking is enabled
 * @returns {Object} passenger - The priority passenger data
 */
const getPriorityPassengerData = ({
  hasSeats,
  seatsNeeded,
  searchedPassengers,
  allowsSeatSelection,
  overbooking,
}) => {
  const { purchase, whitelabelConfig, search, siemprePlus } = store.getState();
  const purchaseJS = purchase?.toJS();
  const { passengers = [], walletType, departs, returns, isExchange, busCategories } = purchaseJS;
  const { features } = whitelabelConfig;
  const searchPassengersRaw = search?.get?.('passengers')?.toJS?.() || {};
  const usedSearchPassengersForSlots = shouldUseSearchPassengersForCategorySlots(
    searchedPassengers,
    searchPassengersRaw,
  );
  const selectionForCategories = usedSearchPassengersForSlots
    ? searchPassengersRaw
    : searchedPassengers;

  /**
   * Is an hybrid trip (open and normal ticket), there is not seats selected yet and it is allowed, it is return an
   * empty array because the next way is the one that has to fill the default passengers
   */
  if (isHybridTrip(departs, returns) && !hasSeats && allowsSeatSelection) return [];

  const storedPassengers = getStoredPassengers();

  const purchasePassengers = passengers?.length && passengers;
  let storedPassengersValidated = storedPassengers?.length && storedPassengers;
  // If the way doesn't have seats, the seats saved in local storage are removed
  if (!hasSeats && storedPassengersValidated) {
    storedPassengersValidated.forEach((passenger) => delete passenger.seats);
    // In overbooking scenarios, we need to preserve the correct number of passengers
    // Only limit to 1 passenger if we don't have a specific number of seats needed
    if (!seatsNeeded?.length) {
      storedPassengersValidated = storedPassengersValidated?.slice(0, 1);
    }
  }

  const priorityPassengers = purchasePassengers || storedPassengersValidated || [];

  // If a user is logged in, it is parsed and used as the first passenger.
  // Exception: in Doters flexible redeem mode, if the saved purchase passenger is a
  // different person than the account holder, preserve the saved passenger instead of
  // overwriting — the user intentionally chose to travel as a guest (MSI-1289).
  // Also skipped when Viaja Ahorrando is active (vivaPnr in purchase meta) to
  // preserve the passenger data from the Viva reservation.
  const { user: siemprePlusUser } = siemprePlus?.toJS?.() || {};
  const effectiveWalletType = walletType || siemprePlusUser?.loyaltyName?.toLowerCase();
  const loggedUser = getUserByWalletType(effectiveWalletType);
  const state = store.getState();
  const vivaPnr = getVivaPnrFromPurchase(state);
  const hasViajaAhorrandoActive = Boolean(vivaPnr);
  const isFlexibleMode = isDotersFlexibleRedeemEnabled({
    flexibleMatchingConfig: features?.DOTERS_FLEXIBLE_PASSENGER_MATCHING,
    walletType: effectiveWalletType,
  });
  const savedPassengerIsNonHolder =
    isFlexibleMode &&
    purchasePassengers &&
    isAccountHolder({ purchase: { passengers: purchasePassengers }, user: siemprePlusUser }) ===
      false;

  if (!isExchange && loggedUser && !savedPassengerIsNonHolder && !hasViajaAhorrandoActive) {
    const parsedLoggedUser = createPassenger({
      isFirstPassenger: true,
      passenger: purchasePassengers
        ? {
            ...loggedUser,
            id: purchasePassengers[0].id,
            category: priorityPassengers[0].category,
            busCategory: priorityPassengers[0].busCategory,
          }
        : loggedUser,
    });
    priorityPassengers[0] = parsedLoggedUser;
  }

  let countableCategories =
    selectionForCategories &&
    getCountableCategories({ categories: decamelizeKeys(selectionForCategories) });

  if (countableCategories?.length && !overbooking && !isExchange) {
    countableCategories = sortPassengerSlotsByFarePriority(countableCategories);
  }

  const passengersData = priorityPassengers.length ? priorityPassengers : [{}];

  const seatsQuantity = hasSeats && seatsNeeded.length;

  // Calculate the target number of passengers
  let targetPassengerCount = 0;
  if (hasSeats && seatsQuantity) {
    targetPassengerCount = seatsQuantity;
  } else if (overbooking && seatsNeeded?.length) {
    targetPassengerCount = seatsNeeded.length;
  } else {
    targetPassengerCount = countableCategories.length;
  }

  // In overbooking scenarios, we need to limit passengers to the exact count needed
  if (overbooking && passengersData.length > targetPassengerCount) {
    passengersData.splice(targetPassengerCount);
  }

  // Expand passengers array if needed
  if (passengersData.length < targetPassengerCount) {
    const timesNumber = targetPassengerCount - passengersData.length;
    times(timesNumber, () => passengersData.push({}));
  }

  passengersData.forEach((passenger, index) => {
    // Checks if the passenger from the purchase already has a category to used or used the one from the selection
    let passengerBusCategory = passenger?.busCategory;

    if (countableCategories && !overbooking && !isExchange) {
      let searchedCategory = countableCategories[index];

      // If the category is pet, it is checked if pet seats are selected
      // Only apply this logic if AUTOCHECK_PET_FRIENDLY_SEAT_ENABLED is false (old behavior)
      if (
        !features.PASSENGER_PET_AUTOCHECK_ENABLED &&
        hasSeats &&
        searchedCategory === 'pet_friendly'
      ) {
        searchedCategory = DEFAULT_CATEGORY;
      }

      if (searchedCategory !== undefined) {
        passengerBusCategory = searchedCategory;
      }
    } else if (overbooking) {
      passengerBusCategory = OVERBOOKED_CATEGORY;
    }

    passenger.busCategory = getPassengerCategory({
      passengerBusCategory,
      busCategories,
      index,
    });
  });

  return passengersData;
};

/**
 * Adds departureSeats or returnSeats to each passenger, according to the way property.
 *
 * @returns {Array} A new instance of the passengers array with the departureSeats or returnSeats added
 *
 * @param {Object} params - An object with the follogin properties:
 * @param {String} params.way - The way of the trip (departure or return)
 * @param {Array} params.passengers - The passengers to add the seats
 * @param {Array} params.trips - The trips of the way
 * @param {Array} params.seats - The seats to assign to the passengers. Each seat should have a tripSlug property
 *
 * The departureSeats or returnSeats property will be added to each passenger, with the following structure:
 *
 * ```js
 * passenger.departureSeats = {
 *   "tripSlug1": seat,
 *   "tripSlug2": seat
 * }
 *
 * passenger.returnSeats = {
 *   "tripSlug1": seat,
 *   "tripSlug2": seat
 * }
 * ```
 */
const addWaySeatsPerPassenger = ({ way, passengers, trips, seats }) => {
  /**
   * seatsByPassenger has the following structure:
   *
   * ```js
   * [
   *   [0]: {
   *     [tripSlug1]: seat
   *     [tripSlug2]: seat
   *   },
   *   [1]: {
   *     [tripSlug1]: seat
   *     [tripSlug2]: seat
   *   }
   * ]
   * ```
   */
  const seatsByPassenger = passengers.reduce((acc, passenger, passengerIndex) => {
    const passengerSeatsByTrip = trips.reduce((acc, trip) => {
      const tripSeats = seats.filter((seat) => seat.tripSlug === trip.trip.id);
      return {
        ...acc,
        [trip.trip.id]: tripSeats[passengerIndex],
      };
    }, {});
    return {
      ...acc,
      [passengerIndex]: passengerSeatsByTrip,
    };
  }, []);
  return passengers.map((passenger, index) => {
    return {
      ...passenger,
      ...(way === 'departure' ? { departureSeats: seatsByPassenger[index] } : {}),
      ...(way === 'return' ? { returnSeats: seatsByPassenger[index] } : {}),
    };
  });
};

/**
 * Returns the initial passengers data
 * @param {Object} params - The parameters object
 * @param {boolean} params.allowsSeatSelection - Whether seat selection is allowed
 * @param {Array} params.departureSelectedSeats - Selected seats for departure
 * @param {Array} params.returnSelectedSeats - Selected seats for return
 * @param {Object} params.passengerSelection - Passenger selection data
 * @param {Array} params.departureTrips - Departure trips
 * @param {Array} params.returnTrips - Return trips
 * @param {boolean} params.overbooking - Whether overbooking is enabled
 * @returns {Array} passengers - The initial passengers data
 */
export const getInitialPassengers = ({
  allowsSeatSelection,
  departureSelectedSeats = [],
  returnSelectedSeats = [],
  passengerSelection,
  departureTrips = [],
  returnTrips = [],
  overbooking,
}) => {
  const cleanedDepartureSeats = removeAdjacentSeats(departureSelectedSeats);
  const cleanedReturnSeats = removeAdjacentSeats(returnSelectedSeats);

  // Number of passengers for the purchase ---------------
  const departureTripsLength = departureTrips.length || 1;
  /**
   * Since trips can have connections. The general formula to calculate the number of passengers is:
   * - Number of seats selected / Number of trips
   *
   * This works for both departure and return trips. And also for direct trips or trips with connections.
   */
  let selectedSeatsNeeded = Array(cleanedDepartureSeats.length / departureTripsLength).fill({});

  // If the departure seats are not present, the return seats are used to create the default passengers data
  if (!selectedSeatsNeeded?.length && cleanedReturnSeats?.length) {
    const returnTripsLength = returnTrips.length || 1;
    selectedSeatsNeeded = Array(cleanedReturnSeats.length / returnTripsLength).fill({});
  }
  // Number of passengers for the purchase ---------------

  // The passengers to prefill are got based on the data priority
  const hasSeats = allowsSeatSelection && selectedSeatsNeeded.length;

  // get passengers from purchase or local storage.
  // If there are no passengers, an empty array is returned
  const passengersToUse = getPriorityPassengerData({
    hasSeats,
    seatsNeeded: selectedSeatsNeeded,
    searchedPassengers: passengerSelection,
    allowsSeatSelection,
    overbooking,
  });
  let formattedPassengers;

  if (hasSeats) {
    // If it has seats, the passengers data is created with the seats selected
    formattedPassengers = selectedSeatsNeeded.map((item, index) => {
      return createPassenger({
        isFirstPassenger: index === 0,
        passenger: {
          ...passengersToUse[index],
        },
      });
    });

    formattedPassengers = getSelectedSeatForUI({
      passengers: formattedPassengers,
      departSeats: cleanedDepartureSeats,
      returnSeats: cleanedReturnSeats,
    });

    const departureHasConnections = departureTrips.length > 1;
    if (departureHasConnections) {
      formattedPassengers = addWaySeatsPerPassenger({
        way: 'departure',
        passengers: formattedPassengers,
        trips: departureTrips,
        seats: cleanedDepartureSeats,
      });
    }

    const returnHasConnections = returnTrips.length > 1;
    if (returnHasConnections) {
      formattedPassengers = addWaySeatsPerPassenger({
        way: 'return',
        passengers: formattedPassengers,
        trips: returnTrips,
        seats: cleanedReturnSeats,
      });
    }
  } else {
    formattedPassengers = passengersToUse.map((passenger, index) => {
      return createPassenger({
        isFirstPassenger: index === 0,
        passenger,
      });
    });
  }

  return formattedPassengers;
};

/**
 * Returns the list of passengers that are not available in the trips.
 * @param {Object} options - The options object.
 * @param {Object} options.neededPassengers - The needed passengers.
 * @param {Array} options.trips - The trips.
 * @returns {Array} - The list of passengers that are not available in the trips.
 */
export const passengersNotAvailable = ({ neededPassengers, trips }) => {
  /**
   * If some passenger needed is not found in the trips, this passengers is saved to notify
   * that the passengers was not found in this route
   */
  return (
    Object.keys(neededPassengers)?.filter(
      (passenger) =>
        !trips.some((trip) => {
          const { passengerTypes, openTicket } = trip;
          // If adult is include it means it is the old version so this is not needed
          if (passenger === 'adult') return true;

          /**
           * Function to check availability of a specific type
           * @param {String} typeToCheck - The type to check
           * @returns {Boolean} - True if the type has availability, false otherwise
           */
          const hasTypeAvailability = (typeToCheck) =>
            passengerTypes.some((type) => type.type === typeToCheck && type.availability);

          // Check availability of the specific type or the "oversold" type
          const hasAvailability =
            hasTypeAvailability(passenger) || hasTypeAvailability(OVERBOOKED_CATEGORY);

          // If there is availability and it is not open ticket, the passenger is available
          return hasAvailability && !openTicket;
        }),
    ) || []
  );
};

/**
 * Checks if the searched passengers can be filtered.
 * If the searched passengers are more than one type or is general but more than one, it can be filtered.
 * @param {Object} params - The parameters object.
 * @param {Object} params.searchPassengers - The searched passengers.
 * @returns {boolean} - True if the searched passengers can be filtered, false otherwise.
 */
export const searchedPassengersCanBeFiltered = ({ searchPassengers }) =>
  searchPassengers && !(Object.keys(searchPassengers).length === 1 && searchPassengers.general);

const GENDER_NORMALIZATION_MAP = { m: 'male', f: 'female' };

/**
 * Normalize a gender value to its full form.
 * Maps abbreviated API values ('m', 'f') to their full equivalents ('male', 'female').
 * @param {string} gender - Raw gender value from the API or form.
 * @returns {string} Normalized gender value ('male', 'female', or the original value).
 */
export const normalizeGender = (gender) =>
  GENDER_NORMALIZATION_MAP[gender?.toLowerCase()] || gender || '';
